import axios from 'axios';
import { Form } from 'formik';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SEND_RECEIPT_EXPRESS_SERVICES } from '../../../constants/paths/api';
import { getCurrentLangPretty } from '../../../helpers/url/urlHelpers';
import useScrollToFirstFormikError from '../../../hooks/useScrollToFIrstFormikError';
import ISendReceipt from '../../../interfaces/sendReceipt/sendReceipt';
import Button from '../../base/button';
import FormikFieldWrapper from '../../base/forms/formikFieldWrapper';
import { A } from '../../base/typography';
import UploadFiles from '../../uploadFiles';
import DefaultService from './defaultService';
import Delivery from './delivery';
import Info from './info';
import InstructionsToEmployee from './instructionsToEmployee';
import * as StyledSendReceiptForm from './sendReceiptForm.styles';
import TrackingNumber from './trackingNumber';

interface Props {
	values: ISendReceipt;
	setFieldTouched: any;
	setFieldValue: any;
	isValid: any;
	isSubmitting: boolean;
	errors: any;
}

const SendReceiptForm: FC<Props> = ({ values, isSubmitting, setFieldTouched, setFieldValue, isValid, errors }) => {
	const { t } = useTranslation('sendReceipt');
	const lang = getCurrentLangPretty();
	const [deliveryServices, setDeliveryServices] = useState([DefaultService[lang]]);
	const [hasFetchedServices, setHasFetchedServices]: [[string, number], any] = useState(['', 0]);
	const { identificationNumber, foreignSsn, trackingNumbers, hasReceipt } = values;
	const { socialId: socialIdErrors } = errors;
	const ref = useRef(null);
	useScrollToFirstFormikError(errors, isSubmitting, isValid, ref);

	// Fetch express services
	useEffect(() => {
		if (
			identificationNumber.length >= 10 && // Valid socialID
			!socialIdErrors && // No errors
			hasReceipt === 'Yes' && // Has to have receipt
			(hasFetchedServices[0] !== identificationNumber || hasFetchedServices[1] !== trackingNumbers.length)
			// If either the socialId or added/removed trackingnumbers then we refetch the services
		) {
			setHasFetchedServices([identificationNumber, trackingNumbers.length]);
			axios({
				url: `${SEND_RECEIPT_EXPRESS_SERVICES}`,
				params: {
					identificationNumber: identificationNumber,
					language: lang,
					trackingnumbers: trackingNumbers,
				},
			}).then(res => {
				res.data.unshift(DefaultService[lang]);
				setDeliveryServices(res.data);
			});
		} else if ((foreignSsn || hasReceipt === 'No' || socialIdErrors !== undefined) && deliveryServices.length > 1) {
			setHasFetchedServices('');
			setDeliveryServices([DefaultService[lang]]);
			setFieldValue('deliveryService', '0');
		}
	}, [
		lang,
		deliveryServices,
		identificationNumber,
		socialIdErrors,
		foreignSsn,
		hasFetchedServices,
		hasReceipt,
		setFieldValue,
		trackingNumbers,
	]);

	return (
		<div ref={ref}>
			<Form>
				<TrackingNumber values={values} errors={errors} setFieldValue={setFieldValue} noQuery={true} />
				{trackingNumbers && trackingNumbers.length > 0 && (
					<StyledSendReceiptForm.Wrapper>
						{trackingNumbers && new RegExp('PR.{9}YP', 'i').test(trackingNumbers.join()) && (
							<StyledSendReceiptForm.InfoText>
								{t('infoText')}
								<A blank to={'http://' + t('infoTextLink')}>
									{t('infoTextLink')}
								</A>
							</StyledSendReceiptForm.InfoText>
						)}
						<Info values={values} setFieldValue={setFieldValue} />
						<FormikFieldWrapper
							name="additionalInformation"
							languageComponent="sendReceipt"
							smallLabel
							label={`${t('contentDescription')} *`}>
							<StyledSendReceiptForm.StyledTextArea
								maxLength="254"
								type="text"
								id="additionalInformation"
								name="additionalInformation"
								component="textarea"
								padding="1rem"
								placeholder={`${t('contentsDescription')}`}
							/>
						</FormikFieldWrapper>
						<InstructionsToEmployee
							values={values}
							setFieldValue={setFieldValue}
							setFieldTouched={setFieldTouched}
						/>
						<UploadFiles name="files" />
						<Delivery
							services={deliveryServices}
							values={values}
							setFieldTouched={setFieldTouched}
							setFieldValue={setFieldValue}
							isValid={isValid}
							errors={errors}
						/>
						<StyledSendReceiptForm.ButtonWrapper>
							<Button wide primary mobileBlock type="submit" isLoading={isSubmitting}>
								{t('submit')}
							</Button>
						</StyledSendReceiptForm.ButtonWrapper>
					</StyledSendReceiptForm.Wrapper>
				)}
			</Form>
		</div>
	);
};

export default SendReceiptForm;
